import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchArticles } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { PullQuotePrimary } from "@/components/PullQuote";
import { QUOTES } from "@/lib/quotes";
import { useSEO, SEO } from "@/lib/seo";

const CATEGORIES = [
    "All",
    "Life After Diagnosis",
    "Rebuilding Trust in the Body",
    "Finding My Voice",
    "Building Valanti Labs",
    "ALATI and Family",
    "Music",
    "Lessons From Adversity",
    "Beginning Again",
];

export default function Journal() {
    useSEO(SEO.journal);
    const [articles, setArticles] = useState([]);
    const [category, setCategory] = useState("All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        fetchArticles(category === "All" ? undefined : category)
            .then(setArticles)
            .finally(() => setLoading(false));
    }, [category]);

    return (
        <main>
            <section className="bg-ivory text-charcoal pt-36 md:pt-44 pb-16 md:pb-24">
                <div className="container-editorial">
                    <p className="eyebrow text-oxblood">Journal</p>
                    <h1 className="display-1 mt-8 max-w-4xl">
                        Letters from a life <span className="italic">being rebuilt.</span>
                    </h1>
                    <p className="mt-10 body-lead text-charcoal/70 max-w-2xl">
                        Personal reflections, founder updates and quiet observations from the long work of
                        beginning again. These drafts are clearly labelled while the journal is being shaped.
                    </p>
                </div>
            </section>

            {/* Category filter */}
            <section className="bg-ivory">
                <div className="container-editorial border-t border-charcoal/10 py-8">
                    <div
                        data-testid={TID.journal.categoryFilter}
                        className="flex flex-wrap gap-x-6 gap-y-3"
                    >
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCategory(c)}
                                data-testid={`journal-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`eyebrow transition-colors ${
                                    c === category
                                        ? "text-oxblood"
                                        : "text-charcoal/50 hover:text-charcoal"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* List */}
            <section className="bg-ivory">
                <div className="container-editorial py-12 md:py-20">
                    {loading ? (
                        <p className="text-stone">Loading…</p>
                    ) : articles.length === 0 ? (
                        <p className="text-stone">No articles in this category yet.</p>
                    ) : (
                        <div data-testid={TID.journal.list} className="divide-y divide-charcoal/10">
                            {articles.map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/journal/${a.slug}`}
                                    data-testid={`${TID.journal.card}-${a.slug}`}
                                    className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 hover:bg-ivory-soft transition-colors"
                                >
                                    <div className="md:col-span-3">
                                        <p className="eyebrow text-oxblood">{a.category}</p>
                                        <p className="mt-3 text-sm text-stone">
                                            {a.draft ? "Draft" : new Date(a.published_at).toLocaleDateString()} ·{" "}
                                            {a.reading_time} min read
                                        </p>
                                    </div>
                                    <div className="md:col-span-7">
                                        <h2 className="display-4 group-hover:text-oxblood transition-colors">
                                            {a.title}
                                        </h2>
                                        <p className="mt-4 body-base text-charcoal/70 line-clamp-2">{a.excerpt}</p>
                                    </div>
                                    <div className="md:col-span-2 flex md:justify-end items-start">
                                        <span className="eyebrow text-charcoal/60 group-hover:text-oxblood inline-flex items-center gap-2">
                                            Read <ArrowRight size={14} strokeWidth={1.5} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <PullQuotePrimary>{QUOTES.readerInvitation}</PullQuotePrimary>
        </main>
    );
}
